import {
  Content,
  PageWithHeader,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useStarredEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Link } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import {
  getApiDefinitionRoute,
  getApiType,
  getEntityRoute,
  getEntityApiSummaries,
  normalizeOwnerRef,
  TeamApiRelation,
} from './catalogDiscovery';
import { useCatalog } from './hooks/useCatalog';

type ApiDefinitionFilter = 'all' | 'openapi' | 'asyncapi';

const API_DEFINITION_FILTERS: {
  value: ApiDefinitionFilter;
  label: string;
}[] = [
  { value: 'all', label: 'Todas' },
  { value: 'openapi', label: 'OpenAPI' },
  { value: 'asyncapi', label: 'AsyncAPI' },
];

const getBorderColor = (theme: Theme) =>
  (theme.palette as any).border || theme.palette.divider;

const getNavigationBackground = (theme: Theme) =>
  (theme.palette as any).navigation?.background ||
  theme.palette.background.paper;

const getHeaderTitleColor = (theme: Theme) =>
  theme.palette.info?.light || theme.palette.primary.main;

const getLinkColor = (theme: Theme) =>
  (theme.palette as any).link || theme.palette.primary.main;

const getLinkHoverColor = (theme: Theme) =>
  (theme.palette as any).linkHover || theme.palette.primary.dark;

const getInfoTextColor = (theme: Theme) =>
  (theme.palette as any).infoText || theme.palette.info.dark;

const getInfoBackgroundColor = (theme: Theme) =>
  (theme.palette as any).infoBackground || theme.palette.background.default;

const getSuccessBackgroundColor = (theme: Theme) =>
  (theme.palette as any).successBackground || theme.palette.background.default;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    overflowX: 'hidden',
    width: '100%',
    '& header': {
      backgroundColor: `${getNavigationBackground(theme)} !important`,
      backgroundImage: 'none !important',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: `${getHeaderTitleColor(theme)} !important`,
    },
  },
  content: {
    maxWidth: '1400px !important',
    width: '100% !important',
    padding: `${theme.spacing(3)}px !important`,
    margin: '0 auto !important',
    '& > *': {
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    },
    '& .MuiPaper-root': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    '& .MuiTableHead-root': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '& .MuiTableCell-head': {
      color: `${theme.palette.text.primary} !important`,
      fontWeight: 600,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },
    '& .MuiTableCell-body': {
      color: `${theme.palette.text.primary} !important`,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      maxWidth: '300px',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '& a': {
      color: `${getLinkColor(theme)} !important`,
      textDecoration: 'none',
      fontWeight: 600,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      display: 'inline-block',
      maxWidth: '100%',
      '&:hover': {
        color: `${getLinkHoverColor(theme)} !important`,
        textDecoration: 'underline',
      },
    },
    '& .MuiTable-root': {
      tableLayout: 'auto',
      width: '100%',
      minWidth: 'auto',
    },
    '& .MuiTableContainer-root': {
      overflowX: 'auto',
      maxWidth: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: `${theme.spacing(2)}px !important`,
    },
  },
  teamPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${getBorderColor(theme)}`,
    borderRadius: theme.shape.borderRadius * 2,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  sectionIcon: {
    color: theme.palette.secondary.main,
    flexShrink: 0,
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.25,
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    maxWidth: 620,
  },
  filterPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${getBorderColor(theme)}`,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
  },
  filterTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: '1rem',
  },
  filterControl: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      minHeight: 56,
      '& fieldset': {
        borderColor: getBorderColor(theme),
      },
      '&:hover fieldset': {
        borderColor: theme.palette.secondary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiSelect-select, & .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      minHeight: 56,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
  relationChip: {
    height: 22,
    border: `1px solid ${getBorderColor(theme)}`,
    backgroundColor: getSuccessBackgroundColor(theme),
    color: theme.palette.success.main,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  typeChip: {
    height: 22,
    border: `1px solid ${getBorderColor(theme)}`,
    backgroundColor: getInfoBackgroundColor(theme),
    color: getInfoTextColor(theme),
    fontWeight: 600,
  },
  nameCell: {
    display: 'grid',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  apiList: {
    display: 'grid',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  apiRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    minWidth: 0,
  },
  starButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.warning.main,
    },
  },
  starButtonActive: {
    color: theme.palette.warning.main,
  },
}));

const StarButton = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const { isStarredEntity, toggleStarredEntity } = useStarredEntity(entity);

  return (
    <Tooltip
      title={isStarredEntity ? 'Quitar de favoritos' : 'Agregar a favoritos'}
    >
      <IconButton
        className={`${classes.starButton} ${
          isStarredEntity ? classes.starButtonActive : ''
        }`}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          toggleStarredEntity();
        }}
        size="small"
        aria-label={
          isStarredEntity ? 'Quitar de favoritos' : 'Agregar a favoritos'
        }
      >
        {isStarredEntity ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
    </Tooltip>
  );
};

const formatFilterLabel = (value: string, props?: { allLabel?: string }) => {
  if (value === 'all') return props?.allLabel ?? 'Todos';

  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
};

const relationLabel = (relation: TeamApiRelation) => {
  const labelsMap: Record<TeamApiRelation, string> = {
    owned: 'Propietario',
    provided: 'Proveedor',
    consumed: 'Consumidor',
  };

  return labelsMap[relation] ?? labelsMap.consumed;
};

export const CustomCatalogPage = () => {
  const classes = useStyles();
  const {
    allEntities,
    entities,
    loading,
    kindFilter,
    typeFilter,
    apiDefinitionFilter,
    ownerFilter,
    lifecycleFilter,
    businessUnitFilter,
    optionsFilters,
    setKindFilter,
    setTypeFilter,
    setApiDefinitionFilter,
    setOwnerFilter,
    setLifecycleFilter,
    setBusinessUnitFilter,
  } = useCatalog();

  const monthDiff = (dateFrom: Date, dateTo: Date) => {
    return (
      dateTo.getMonth() -
      dateFrom.getMonth() +
      12 * (dateTo.getFullYear() - dateFrom.getFullYear())
    );
  };

  const renderOwner = (entity: Entity) => {
    const owner = (entity as any).spec?.owner;
    if (!owner || typeof owner !== 'string') {
      return '-';
    }

    const ownerRef = normalizeOwnerRef(owner);
    const group = ownerRef
      ? allEntities.find(entity => {
          return (
            entity.kind.toLowerCase() === 'group' &&
            normalizeOwnerRef(
              `${entity.metadata.namespace || 'default'}/${
                entity.metadata.name
              }`,
            ) === ownerRef
          );
        })
      : undefined;

    if (group) {
      return <Link to={getEntityRoute(group)}>{group.metadata.name}</Link>;
    }

    return owner;
  };

  const renderName = (entity: Entity) => {
    const now = new Date();
    const creationDate = new Date(
      entity.metadata.labels?.['creation-date'] || '',
    );
    const diffInMonths = monthDiff(creationDate, now);

    const showBadget = diffInMonths < 2;
    const name = entity?.metadata?.name;

    return (
      <Box className={classes.nameCell}>
        <Link to={getEntityRoute(entity)}>
          {typeof name === 'string' ? name : '-'}
        </Link>
        {showBadget ? (
          <Chip
            size="small"
            className={classes.typeChip}
            label="Nuevo servicio"
          />
        ) : null}
      </Box>
    );
  };

  const renderApis = (entity: Entity) => {
    const apis = getEntityApiSummaries(entity, allEntities);

    if (apis.length === 0) {
      return '-';
    }

    return (
      <Box className={classes.apiList}>
        {apis.map(api => (
          <Box key={api.ref} className={classes.apiRow}>
            <Link to={getApiDefinitionRoute(api.entity)}>
              {api.entity.metadata.name}
            </Link>
            <Chip
              size="small"
              className={classes.typeChip}
              label={getApiType(api.entity) || 'api'}
            />
            {api.relations.map(relation => (
              <Chip
                key={relation}
                size="small"
                className={classes.relationChip}
                label={relationLabel(relation)}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const columns: TableColumn<Entity>[] = [
    {
      title: '',
      field: 'starred',
      render: (entity: Entity) => <StarButton entity={entity} />,
      width: '40px',
      sorting: false,
    },
    {
      title: 'Nombre',
      field: 'metadata.name',
      render: renderName,
    },
    {
      title: 'Owner',
      field: 'spec.owner',
      render: renderOwner,
    },
    {
      title: 'BU',
      field: 'metadata.labels.business-unit',
      render: (entity: Entity) =>
        entity.metadata.labels?.['business-unit'] || '-',
    },
    {
      title: 'APIs',
      field: 'spec.providesApis',
      render: renderApis,
    },
    {
      title: 'Categoría',
      field: 'spec.type',
      render: (entity: Entity) => (entity as any).spec?.type || entity.kind,
    },
    {
      title: 'Descripción',
      field: 'metadata.description',
      render: (entity: Entity) => entity.metadata.description || '-',
    },
  ];

  return (
    <div className={classes.header}>
      <PageWithHeader title="Catalog" themeId="tool">
        <Content className={classes.content}>
          <Paper elevation={0} className={classes.filterPanel}>
            <Typography variant="h6" className={classes.filterTitle}>
              Filtros
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={kindFilter}
                    onChange={e => setKindFilter(e.target.value as string)}
                    label="Tipo"
                  >
                    {optionsFilters.kinds.map(kind => (
                      <MenuItem key={kind} value={kind}>
                        {formatFilterLabel(kind)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value as string)}
                    label="Categoría"
                  >
                    {optionsFilters.types.map(type => (
                      <MenuItem key={type} value={type}>
                        {formatFilterLabel(type, { allLabel: 'Todas' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>API</InputLabel>
                  <Select
                    value={apiDefinitionFilter}
                    onChange={e =>
                      setApiDefinitionFilter(
                        e.target.value as ApiDefinitionFilter,
                      )
                    }
                    label="API"
                  >
                    {API_DEFINITION_FILTERS.map(filter => (
                      <MenuItem key={filter.value} value={filter.value}>
                        {formatFilterLabel(filter.label, { allLabel: 'Todas' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Propietario</InputLabel>
                  <Select
                    value={ownerFilter}
                    onChange={e => setOwnerFilter(e.target.value as string)}
                    label="Propietario"
                  >
                    {optionsFilters.owners.map(owner => (
                      <MenuItem key={owner} value={owner}>
                        {formatFilterLabel(owner)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Ciclo de Vida</InputLabel>
                  <Select
                    value={lifecycleFilter}
                    onChange={e => setLifecycleFilter(e.target.value as string)}
                    label="Ciclo de Vida"
                  >
                    {optionsFilters.lifecycles.map(lifecycle => (
                      <MenuItem key={lifecycle} value={lifecycle}>
                        {formatFilterLabel(lifecycle)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Unidad de Negocio</InputLabel>
                  <Select
                    value={businessUnitFilter}
                    onChange={e =>
                      setBusinessUnitFilter(e.target.value as string)
                    }
                    label="Unidad de Negocio"
                  >
                    {optionsFilters.businessUnits.map(unit => (
                      <MenuItem key={unit} value={unit}>
                        {formatFilterLabel(unit, { allLabel: 'Todas' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Box style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
            <Table
              title={`Entities (${entities.length})`}
              options={{
                search: true,
                paging: true,
                pageSize: 20,
                padding: 'dense',
                tableLayout: 'auto',
              }}
              columns={columns}
              data={entities}
              isLoading={loading}
            />
          </Box>
        </Content>
      </PageWithHeader>
    </div>
  );
};
